import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { ip_back } from "../config.js";
import { useBrand } from "../context/BrandContext";
import { checkEmailAndSendConfirmation } from "./funtionsBD/Authservice";
import { normalizeCountryCode } from "../utils/clubAdminOnboarding";
import { applyInvitePageLanguage, clubCountryLanguage, markInviteUiLangOverride } from "../utils/clubCountryLanguage";
import { LanguageShortcutFab } from "./LanguageShortcutFab";
import { completeClubAdminRegistration } from "../utils/completeClubAdminRegistration";
import { usePermissions } from "../context/Permissioncontext";
import { useWizardViewportHeight } from "../hooks/useWizardViewportHeight";
import { useIsMobile, useCoarsePointer } from "../hooks/useIsMobile";
import {
  EMPTY_SCFORH_SEED,
  SCFORH_CURRENT_OFFER,
  SCFORH_HEALTH_DIMENSIONS,
  SCFORH_INTEGRATION,
  SCFORH_SPORTING_AIMS,
  SCFORH_TARGET_GROUPS,
  inferHealthDimensionStatus,
  syncScforhApplyIntegration,
} from "../constants/scforhClubHealth";
import { CLUB_ADMIN_COUNTRIES } from "../constants/clubAdminCountries";
import {
  ADMIN_POSITIONS,
  EMPTY_ADMIN_FORM,
  STEP_PROMPT_KEY,
  STEP_TO_PHASE,
  getClubAdminSteps,
  getPositionName,
  isEuropeWalkIn,
  looksLikeClubNotListed,
} from "../digiping/clubAdminWizard";
import { DigipingMark } from "../digiping";
import { LAST_BRAND_CLUB_ID_KEY } from "../utils/localStorageBrand";
import { resolveClubImageUrl } from "../utils/resolveClubImageUrl";
import { decodeJwtPayload } from "../utils/inviteClubId";
import smartttDefaultLogo from "../assets/smarttt-default-logo.png";
import { RichText } from "./AICoach/digipingRichText";
import ScforhApproachIntro from "./ScforhApproachIntro";
import SportClubsOneHealthIntro from "./SportClubsOneHealthIntro";
import ScforhWhoCapsule from "./ScforhWhoCapsule";
import ScforhDimensionStatus from "./ScforhDimensionStatus";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SecurityIcon from "@mui/icons-material/Security";
import "./AIProfilingWizard.css";
import "./ClubAdminProfiling.css";

const TYPING_MS = 420;

function ClubAdminTypewriter({
  text,
  onComplete,
  onTick,
  msPerChar = 11,
  className = "msg-content-text typewriter-text",
  richWhileStreaming = true,
}) {
  const [visibleCount, setVisibleCount] = useState(0);
  const completedRef = useRef(false);

  useEffect(() => {
    completedRef.current = false;
    setVisibleCount(0);
    if (!text) {
      completedRef.current = true;
      onComplete?.();
      return undefined;
    }
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setVisibleCount(i);
      onTick?.();
      if (i >= text.length) {
        clearInterval(id);
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete?.();
        }
      }
    }, msPerChar);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const full = String(text || "");
  const done = visibleCount >= full.length && full;
  if (done) return <RichText text={full} className={className} />;
  let slice = full.slice(0, Math.max(0, visibleCount));
  if ((slice.match(/\*\*/g) || []).length % 2 === 1) slice += "**";
  return (
    <div className={`${className} typewriter-streaming`.trim()}>
      {richWhileStreaming ? <RichText text={slice} /> : <span>{slice}</span>}
      {visibleCount < full.length ? (
        <span className="typewriter-caret" aria-hidden="true" />
      ) : null}
    </div>
  );
}

function InviteSplashLogos({ club, clubName }) {
  const clubUrl = resolveClubImageUrl(club?.club_image);
  return (
    <div
      className="invite-splash-logos"
      role="img"
      aria-label={[clubName, "SmartTTClub"].filter(Boolean).join(" · ")}
    >
      {clubUrl ? (
        <img src={clubUrl} alt={clubName || ""} className="invite-splash-logos__club" />
      ) : null}
      <img src={smartttDefaultLogo} alt="SmartTTClub" className="invite-splash-logos__smarttt" />
    </div>
  );
}

/** DigiPing avatar: real club logo only. Never the platform shield/favicon. */
function DigipingWizardMark({ clubLogoSrc, size, imgClassName = "", title = "Pingflo" }) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    setImgFailed(false);
  }, [clubLogoSrc]);
  if (clubLogoSrc && !imgFailed) {
    return (
      <img
        src={clubLogoSrc}
        alt=""
        title={title}
        className={imgClassName}
        onError={() => setImgFailed(true)}
      />
    );
  }
  return <DigipingMark size={size} title={title} />;
}

const ClubAdminProfiling = () => {
  const { t, i18n } = useTranslation();
  const { refreshBrand, brand } = useBrand();
  const { loadPermissions } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  useWizardViewportHeight();
  const isMobileLayout = useIsMobile();
  const isTouchPrimary = useCoarsePointer();
  const autoFocusField = !isMobileLayout && !isTouchPrimary;
  const isDualRole = location.state?.isDualRole || false;
  const existingEmail = location.state?.existingEmail || "";

  const [formData, setFormData] = useState({
    ...EMPTY_ADMIN_FORM,
    adminEmail: isDualRole ? existingEmail : "",
    scforh: { ...EMPTY_SCFORH_SEED },
  });
  const formRef = useRef(formData);
  formRef.current = formData;

  const emailResumePending = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (new URLSearchParams(location.search).get("invitation_token")) return false;
    try {
      const token = localStorage.getItem("emailConfirmationToken");
      const pending = localStorage.getItem("pendingEmail");
      if (!token || !pending) return false;
      const payload = decodeJwtPayload(token);
      if (!payload?.exp || payload.exp <= Date.now() / 1000) return false;
      return !!(payload.isClubAdmin || payload.clubData);
    } catch {
      return false;
    }
  }, [location.search]);

  const [introStarted, setIntroStarted] = useState(() => emailResumePending);
  const [introPhase, setIntroPhase] = useState("body");
  const [introSmartIdx, setIntroSmartIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [wizardStep, setWizardStep] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const [textDraft, setTextDraft] = useState("");
  const [multiDraft, setMultiDraft] = useState([]);
  const [clubNotListed, setClubNotListed] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [clubSearch, setClubSearch] = useState("");
  const [clubs, setClubs] = useState([]);
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [emailCheckError, setEmailCheckError] = useState(null);
  const [emailCheckErrorCode, setEmailCheckErrorCode] = useState(null);

  const clearEmailCheckError = useCallback(() => {
    setEmailCheckError(null);
    setEmailCheckErrorCode(null);
  }, []);

  const showEmailCheckError = useCallback((message, code = null) => {
    setEmailCheckError(message || t("digipingAdminEmailInvalid"));
    const msg = String(message || "");
    const inferred =
      code ||
      (/already registered as a Club Admin/i.test(msg)
        ? "EMAIL_ALREADY_CLUB_ADMIN"
        : /already registered with both Admin and Player/i.test(msg)
          ? "EMAIL_ALREADY_BOTH_ROLES"
          : /already registered|already in use|please login/i.test(msg)
            ? "EMAIL_ALREADY_REGISTERED"
            : null);
    setEmailCheckErrorCode(inferred);
  }, [t]);

  const renderEmailCheckError = () => {
    if (!emailCheckError) return null;
    const showLogin =
      emailCheckErrorCode === "EMAIL_ALREADY_CLUB_ADMIN" ||
      emailCheckErrorCode === "EMAIL_ALREADY_BOTH_ROLES" ||
      emailCheckErrorCode === "EMAIL_ALREADY_REGISTERED" ||
      /already registered|please login|login with this account/i.test(
        String(emailCheckError)
      );
    return (
      <p className="inline-error-text">
        <span>{emailCheckError}</span>
        {showLogin ? (
          <>
            {" "}
            <Link to="/login" className="inline-error-login-link">
              {t("goToLogin") || "Go to login"}
            </Link>
          </>
        ) : null}
      </p>
    );
  };
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [interestSent, setInterestSent] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(() =>
    Boolean(new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("invitation_token"))
  );
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ password: "", confirm: "" });
  const [isExistingAccount, setIsExistingAccount] = useState(() => Boolean(formRef.current?.isExistingAccount));

  useEffect(() => {
    if (inviteLoading) {
      document.body.removeAttribute("data-digiping-ready");
      return undefined;
    }
    document.body.setAttribute("data-digiping-ready", "1");
    return () => document.body.removeAttribute("data-digiping-ready");
  }, [inviteLoading]);
  const chatEndRef = useRef(null);
  const chatHistoryRef = useRef(null);
  const askTimerRef = useRef(null);

  const steps = useMemo(() => getClubAdminSteps(formData), [
    formData.invitationToken,
    formData.inviteRole,
    formData.userType,
    formData.clubCountry,
    formData.clubCountryCode,
    formData.federationAffiliated,
    formData.isExistingAccount,
    formData.adminFirstName,
    formData.adminLastName,
    formData.adminPosition,
  ]);
  const phase = STEP_TO_PHASE[wizardStep] || "C";
  const joinClubName =
    formData.selectedClub?.club_name ||
    formData.selectedClub?.name ||
    formData.clubName ||
    "";
  const clubLogoSrc = resolveClubImageUrl(formData.selectedClub?.club_image);

  const promptFor = useCallback(
    (step, data = formRef.current) => {
      const key = STEP_PROMPT_KEY[step];
      if (!key) return "";
      if (step === "invitedClub") {
        return i18n.t(key, {
          club: data.selectedClub?.club_name || data.selectedClub?.name || data.clubName || "",
        });
      }
      if (step === "emailConfirmation") {
        return i18n.t(key, { email: data.adminEmail });
      }
      if (step === "scforhHealthDimensions") {
        const status = inferHealthDimensionStatus(data.scforh);
        return i18n.t(key, {
          snapshot: i18n.t(`scforhDimStatus_${status.snapshotKey}`),
        });
      }
      if (step === "adminPassword" && data.restoringDeletedAccount) {
        return i18n.t("digipingAdminQPasswordRestored");
      }
      if (
        step === "adminPassword" &&
        (data.isExistingAccount || isExistingAccount) &&
        !data.restoringDeletedAccount
      ) {
        return i18n.t("digipingAdminRecognizedAccount", {
          email: data.adminEmail || data.email || "",
          club: data.selectedClub?.club_name || data.selectedClub?.name || data.clubName || "",
        });
      }
      if (step === "adminPassword" && data.invitationToken) {
        return i18n.t("digipingAdminQPasswordInvited");
      }
      return i18n.t(key);
    },
    [i18n, isExistingAccount]
  );

  const persist = (data) => {
    const {
      password,
      passwordConfirmation,
      adminPassword,
      adminPasswordConfirm,
      ...safe
    } = data || {};
    const payload = {
      ...safe,
      userType: safe.inviteRole === "committee" ? "committee" : "club_admin",
      ...(isDualRole ? { isDualRole: true } : {}),
    };
    localStorage.setItem("profilingFormData", JSON.stringify(payload));
    if (safe.adminEmail) localStorage.setItem("pendingEmail", safe.adminEmail);
  };

  const scrollToEnd = (behavior = "smooth") => {
    requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({ behavior, block: "end" });
    });
  };

  useEffect(() => {
    scrollToEnd(streamingMsgId ? "auto" : "smooth");
  }, [messages, isTyping, streamingMsgId, wizardStep]);

  const askStep = useCallback(
    (step, data = formRef.current, delay = TYPING_MS) => {
      if (askTimerRef.current) clearTimeout(askTimerRef.current);
      setWizardStep(step);
      setTextDraft("");
      setMultiDraft([]);
      if (step === "scforhHealthDimensions") {
        const saved = data.scforh?.healthDimensions;
        if (Array.isArray(saved) && saved.length) {
          setMultiDraft(saved);
        } else {
          setMultiDraft(inferHealthDimensionStatus(data.scforh).suggestedSelect);
        }
      }
      if (step === "scforhTargetGroups") {
        const savedGroups = data.scforh?.targetGroups;
        if (Array.isArray(savedGroups) && savedGroups.length) {
          setMultiDraft(savedGroups);
        }
      }
      setClubNotListed(false);
      clearEmailCheckError();
      if (step === "adminFirstName") setTextDraft(data.adminFirstName || "");
      if (step === "adminLastName") setTextDraft(data.adminLastName || "");
      if (step === "adminEmail") setTextDraft(data.adminEmail || "");
      if (step === "adminPassword") setPasswordForm({ password: "", confirm: "" });
      setIsTyping(true);
      askTimerRef.current = setTimeout(() => {
        setIsTyping(false);
        const id = `ai_${Date.now()}`;
        setMessages((prev) => [
          ...prev,
          {
            id,
            sender: "ai",
            text: promptFor(step, data),
            step,
            dimension: STEP_TO_PHASE[step],
          },
        ]);
        setStreamingMsgId(id);
      }, delay);
    },
    [promptFor, clearEmailCheckError]
  );

  const assertClubAdminReady = (data = formRef.current) => {
    if (!data.adminEmail || !data.adminFirstName || !data.adminLastName) {
      throw new Error("Missing required fields: email, first name, or last name");
    }
    if (!data.clubCountry) throw new Error("Club country is required");
    if (isEuropeWalkIn(data) && data.federationAffiliated === "yes" && !data.adminProofUrl) {
      throw new Error(t("clubProofRequired"));
    }
    if (!data.adminPosition) throw new Error("Admin position is required");
  };

  const goAfter = useCallback(
    (data, fromStep) => {
      const nextSteps = getClubAdminSteps(data);
      const idx = nextSteps.indexOf(fromStep);
      const next = idx >= 0 ? nextSteps[idx + 1] : nextSteps[0];
      if (!next) return;
      askStep(next, data);
    },
    [askStep]
  );

  const commit = useCallback(
    (userText, patch, fromStep = wizardStep) => {
      const nextData = { ...formRef.current, ...patch };
      if (patch.scforh) {
        nextData.scforh = syncScforhApplyIntegration({
          ...formRef.current.scforh,
          ...patch.scforh,
        });
      }
      setFormData(nextData);
      formRef.current = nextData;
      persist(nextData);
      setMessages((prev) => [
        ...prev,
        {
          id: `user_${Date.now()}`,
          sender: "user",
          text: userText,
          step: fromStep,
        },
      ]);
      goAfter(nextData, fromStep);
    },
    [goAfter, wizardStep]
  );

  const loadClubsForCountry = async (countryCodeOrName) => {
    const code = normalizeCountryCode(countryCodeOrName) || String(countryCodeOrName || "").trim();
    if (!code) {
      setClubs([]);
      return;
    }
    try {
      const response = await axios.get(
        `${ip_back}api/superAdmin/public/getClubsByCountry/${encodeURIComponent(code)}`
      );
      if (response.data.status === "success") {
        setClubs(
          (response.data.data || []).map((club) => ({
            ...club,
            name: club.club_name,
            city: club.city || "",
          }))
        );
      } else {
        setClubs([]);
      }
    } catch {
      setClubs([]);
    }
  };

  useEffect(() => {
    const code = formData.clubCountryCode || normalizeCountryCode(formData.clubCountry);
    if (code) loadClubsForCountry(code);
  }, [formData.clubCountry, formData.clubCountryCode]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("invitation_token");
    if (!token) return undefined;
    let cancelled = false;
    const loadInvite = async () => {
      try {
        setInviteLoading(true);
        const response = await axios.get(`${ip_back}api/club/club-admin-invite`, {
          params: { token },
        });
        if (cancelled) return;
        if (response.data.status !== "success") {
          setError(response.data.message || t("invalidAdminInvite"));
          return;
        }
        const data = response.data.data || {};
        const club = data.club || {};
        if (data.isAlreadyAdmin) {
          setError(t("EMAIL_ALREADY_CLUB_ADMIN") || "You are already registered as an administrator of this club. Please login with this account.");
          return;
        }
        const restoringDeleted = Boolean(data.restoringDeletedAccount);
        const isAccountExisting = Boolean(data.existingAccount) && !restoringDeleted;
        const inviteRole = data.invite_role === "committee" ? "committee" : "admin";
        if (isAccountExisting) {
          setIsExistingAccount(true);
        } else if (restoringDeleted) {
          setIsExistingAccount(false);
        }
        setFormData((prev) => {
          const next = {
            ...prev,
            invitationToken: token,
            inviteRole,
            userType: inviteRole === "committee" ? "committee" : "club_admin",
            isExistingAccount: isAccountExisting,
            restoringDeletedAccount: restoringDeleted,
            adminEmail: data.email || prev.adminEmail,
            adminFirstName: data.first_name || prev.adminFirstName,
            adminLastName: data.last_name || prev.adminLastName,
            clubCountry: club.country || prev.clubCountry,
            clubCountryCode: normalizeCountryCode(club.country) || prev.clubCountryCode,
            clubCity: club.city || prev.clubCity,
            clubAddress: club.club_address || prev.clubAddress,
            clubPostalCode: club.postal_code || prev.clubPostalCode,
            clubPhone: club.phone_mobile || prev.clubPhone,
            clubWebsite: club.club_website || prev.clubWebsite,
            clubDescription: club.club_description || prev.clubDescription,
            clubName: club.club_name || club.name || prev.clubName,
            selectedClub: { ...club, name: club.club_name || club.name },
            hasExistingScforhProfile: Boolean(data.hasScforhProfile),
            hasExistingGroups: Boolean(data.hasExistingGroups),
          };
          formRef.current = next;
          persist(next);
          return next;
        });
        if (data.email) localStorage.setItem("pendingEmail", data.email);
        if (club.club_id) {
          localStorage.setItem(LAST_BRAND_CLUB_ID_KEY, String(club.club_id));
        }
        applyInvitePageLanguage(i18n, club);
        refreshBrand?.();
        setEmailConfirmed(true);
      } catch (inviteError) {
        if (!cancelled) {
          setError(inviteError.response?.data?.message || t("invalidAdminInvite"));
        }
      } finally {
        if (!cancelled) setInviteLoading(false);
      }
    };
    loadInvite();
    return () => {
      cancelled = true;
    };
    // Intentionally omit `t`: switching the club UI to Dutch recreates `t` and
    // would reload the invite forever (clicks do nothing, webpack "message" stall).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const inviteFromUrl = new URLSearchParams(location.search).get("invitation_token");
    if (inviteFromUrl) return;

    const emailConfirmationToken = localStorage.getItem("emailConfirmationToken");
    const pendingEmail = localStorage.getItem("pendingEmail");
    if (!emailConfirmationToken || !pendingEmail) return;

    const tokenData = decodeJwtPayload(emailConfirmationToken);
    if (!tokenData) return;

    const now = Date.now() / 1000;
    if (!(tokenData.exp && tokenData.exp > now)) return;
    const isClubAdmin = !!(tokenData.isClubAdmin || tokenData.clubData || tokenData.pendingId);
    if (!isClubAdmin) return;

    let cancelled = false;

    const applyResume = (parsed, fromToken) => {
      if (cancelled) return;
      const next = {
        ...EMPTY_ADMIN_FORM,
        ...(parsed || {}),
        ...fromToken,
        // Saved wizard answers win over the slim email-token snapshot
        ...(parsed || {}),
        adminEmail: pendingEmail,
        selectedClub: parsed?.selectedClub || fromToken.selectedClub || null,
        clubName:
          parsed?.clubName ||
          fromToken.clubName ||
          parsed?.selectedClub?.club_name ||
          fromToken.selectedClub?.club_name ||
          "",
        clubCountry: parsed?.clubCountry || fromToken.clubCountry || null,
        clubCountryCode: parsed?.clubCountryCode || fromToken.clubCountryCode || "",
        adminFirstName: parsed?.adminFirstName || fromToken.adminFirstName || "",
        adminLastName: parsed?.adminLastName || fromToken.adminLastName || "",
        adminPosition: parsed?.adminPosition || fromToken.adminPosition || "",
        scforh: {
          ...EMPTY_SCFORH_SEED,
          ...(fromToken.scforh || {}),
          ...(parsed?.scforh || {}),
        },
      };

      persist(next);
      setFormData(next);
      formRef.current = next;
      setEmailConfirmed(true);
      setEmailConfirmationSent(true);
      setIntroStarted(true);
      // Link click already confirmed the email — skip re-asking and go to password.
      setWizardStep("adminPassword");
      setMessages([
        {
          id: "resume_password",
          sender: "ai",
          text: t("digipingAdminQPassword"),
          step: "adminPassword",
          dimension: "A",
        },
      ]);
    };

    (async () => {
      let parsed = null;
      try {
        const saved = localStorage.getItem("profilingFormData");
        parsed = saved ? JSON.parse(saved) : null;
      } catch {
        parsed = null;
      }

      const fromToken = tokenData.clubData && typeof tokenData.clubData === "object" ? tokenData.clubData : {};

      // Mobile → desktop: no localStorage. Load full DigiPing snapshot from API.
      const needsServer =
        !parsed ||
        (!parsed.adminFirstName && !parsed.selectedClub && !parsed.clubName && !parsed.scforh);

      if (needsServer) {
        try {
          const apiBase = ip_back.replace(/\/$/, "");
          const res = await axios.get(`${apiBase}/api/user/club-admin-pending`, {
            params: { token: emailConfirmationToken },
          });
          const remote = res.data?.data?.payload;
          if (remote && typeof remote === "object") {
            parsed = { ...(parsed || {}), ...remote };
          }
        } catch (fetchErr) {
          console.warn("club-admin-pending restore failed:", fetchErr?.response?.data || fetchErr.message);
        }
      }

      applyResume(parsed, fromToken);
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally once on mount after confirm-email redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startWizard = () => {
    setIntroStarted(true);
    const first = getClubAdminSteps(formRef.current)[0];
    askStep(first, formRef.current, 280);
  };

  const sendConfirmationEmail = async (data = formRef.current) => {
    const clubData = {
      clubName: data.selectedClub ? data.selectedClub.club_name : data.clubName,
      clubCountry: data.clubCountry,
      clubCountryCode: data.clubCountryCode,
      clubCity: data.clubCity,
      clubAddress: data.selectedClub ? data.selectedClub.club_address : data.clubAddress,
      clubWebsite: data.clubWebsite,
      adminPosition: data.adminPosition,
      adminFirstName: data.adminFirstName,
      adminLastName: data.adminLastName,
      selectedClub: data.selectedClub,
      federationAffiliated: data.federationAffiliated,
      adminProofUrl: data.adminProofUrl,
      scforh: data.scforh,
    };
    try {
      const result = await checkEmailAndSendConfirmation(data.adminEmail, clubData);
      if (result.data && result.data.status === "success") {
        const payload = result.data.data;
        if (payload?.existingAccount || payload?.recognizedUser) {
          clearEmailCheckError();
          localStorage.setItem("pendingEmail", data.adminEmail);
          return { success: true, recognized: true, payload };
        }
        setEmailConfirmationSent(true);
        clearEmailCheckError();
        localStorage.setItem("pendingEmail", data.adminEmail);
        return { success: true, recognized: false };
      }
      showEmailCheckError(
        result.data?.message || result.message || t("digipingAdminEmailInvalid"),
        result.data?.code || result.code || null
      );
      return { success: false };
    } catch (err) {
      showEmailCheckError(
        err.message || err.response?.data?.message || t("digipingAdminEmailInvalid"),
        err.code || err.response?.data?.code || null
      );
      return { success: false };
    }
  };

  const handleEmailSubmit = async () => {
    const email = textDraft.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showEmailCheckError(t("digipingAdminEmailInvalid"));
      return;
    }
    setBusy(true);
    try {
      const nextData = { ...formRef.current, adminEmail: email };
      setFormData(nextData);
      formRef.current = nextData;
      persist(nextData);
      const emailResult = await sendConfirmationEmail(nextData);
      if (!emailResult || !emailResult.success) return;
      setMessages((prev) => [
        ...prev,
        { id: `user_${Date.now()}`, sender: "user", text: email, step: "adminEmail" },
      ]);
      if (emailResult.recognized) {
        const recognizedData = {
          ...nextData,
          isExistingAccount: true,
          adminFirstName: nextData.adminFirstName || emailResult.payload?.firstName || "",
          adminLastName: nextData.adminLastName || emailResult.payload?.lastName || "",
        };
        setFormData(recognizedData);
        formRef.current = recognizedData;
        persist(recognizedData);
        setIsExistingAccount(true);
        askStep("adminPassword", recognizedData);
        return;
      }
      goAfter(nextData, "adminEmail");
    } finally {
      setBusy(false);
    }
  };

  const handleCheckEmail = async () => {
    setBusy(true);
    clearEmailCheckError();
    try {
      const token = localStorage.getItem("emailConfirmationToken");
      const pendingEmail = localStorage.getItem("pendingEmail");
      let confirmed = false;
      if (token && pendingEmail && pendingEmail === formRef.current.adminEmail) {
        const tokenData = decodeJwtPayload(token);
        if (tokenData?.exp && tokenData.exp > Date.now() / 1000) confirmed = true;
      }
      if (!confirmed) {
        showEmailCheckError(t("emailConfirmationNote") || t("digipingAdminQEmailConfirm", { email: formRef.current.adminEmail }));
        return;
      }
      setEmailConfirmed(true);
      setMessages((prev) => [
        ...prev,
        {
          id: `user_${Date.now()}`,
          sender: "user",
          text: t("digipingAdminEmailCheck"),
          step: "emailConfirmation",
        },
      ]);
      goAfter(formRef.current, "emailConfirmation");
    } catch {
      showEmailCheckError(t("digipingAdminEmailInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    const password = passwordForm.password;
    const confirm = passwordForm.confirm;
    if (!password) {
      showEmailCheckError(t("passwordRequired"));
      return;
    }
    if (password.length < 6) {
      showEmailCheckError(t("passwordMinLength"));
      return;
    }
    if (!isExistingAccount || formRef.current?.restoringDeletedAccount) {
      if (!confirm) {
        showEmailCheckError(t("passwordConfirmationRequired"));
        return;
      }
      if (password !== confirm) {
        showEmailCheckError(t("passwordsDoNotMatch"));
        return;
      }
    }
    const data = formRef.current;
    const treatAsExisting =
      (isExistingAccount || data.isExistingAccount) && !data.restoringDeletedAccount;
    try {
      assertClubAdminReady(data);
    } catch (err) {
      showEmailCheckError(err.message);
      return;
    }
    persist(data);
    clearEmailCheckError();
    setMessages((prev) => [
      ...prev,
      { id: `user_${Date.now()}`, sender: "user", text: "••••••", step: "adminPassword" },
      {
        id: `sys_${Date.now()}`,
        sender: "ai",
        text: treatAsExisting ? t("loading") : t("digipingAdminCreatingAccount"),
        isSystem: true,
      },
    ]);
    setBusy(true);
    try {
      const { nextPath } = await completeClubAdminRegistration({
        password,
        profiling: {
          ...data,
          userType: data.inviteRole === "committee" ? "committee" : "club_admin",
          isExistingAccount: treatAsExisting,
          restoringDeletedAccount: Boolean(data.restoringDeletedAccount),
          ...(isDualRole ? { isDualRole: true } : {}),
        },
        i18n,
        loadPermissions,
        refreshBrand,
      });
      setPasswordForm({ password: "", confirm: "" });
      navigate(nextPath);
    } catch (err) {
      showEmailCheckError(
        err.response?.data?.message || err.message || t("registrationFailed"),
        err.response?.data?.code || err.code || null
      );
    } finally {
      setBusy(false);
    }
  };

  const handleProofUpload = async (file) => {
    if (!file) return;
    const isPdf = file.type === "application/pdf" || String(file.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      showEmailCheckError(t("clubProofPdfOnly"));
      return;
    }
    try {
      setUploadingProof(true);
      clearEmailCheckError();
      const payload = new FormData();
      payload.append("proof", file);
      const response = await axios.post(`${ip_back}api/club/club-admin-proof`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status !== "success") {
        throw new Error(response.data.message || "Upload failed");
      }
      commit(file.name, {
        adminProofUrl: response.data.data.url,
        adminProofName: file.name,
      });
    } catch (err) {
      showEmailCheckError(err.response?.data?.message || err.message);
    } finally {
      setUploadingProof(false);
    }
  };

  const handleAffiliationSubmit = async () => {
    const data = formRef.current;
    if (!data.adminEmail || !data.clubName || !data.clubCountry) {
      showEmailCheckError(t("affiliationInterestRequired"));
      return;
    }
    try {
      setBusy(true);
      clearEmailCheckError();
      const response = await axios.post(`${ip_back}api/club/affiliation-interest`, {
        email: data.adminEmail,
        firstName: data.adminFirstName,
        lastName: data.adminLastName,
        clubName: data.clubName,
        clubCountry: data.clubCountry,
        clubCity: data.clubCity,
        phone: data.adminPhone || data.clubPhone,
        website: data.clubWebsite,
        message: data.clubDescription,
      });
      if (response.data.status !== "success") {
        throw new Error(response.data.message || "Request failed");
      }
      setInterestSent(true);
      setMessages((prev) => [
        ...prev,
        {
          id: `sys_${Date.now()}`,
          sender: "ai",
          text: `${t("affiliationInterestSent")}\n\n${t("affiliationInterestSentHint")}`,
          isSystem: true,
        },
      ]);
    } catch (err) {
      showEmailCheckError(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const beginEditLast = () => {
    const last = [...messages].reverse().find((m) => m.sender === "user" && m.step);
    if (!last || isTyping || streamingMsgId) return;
    const idx = messages.findIndex((m) => m.id === last.id);
    setMessages(messages.slice(0, idx));
    setWizardStep(last.step);
    setStreamingMsgId(null);
  };

  const isInvited = Boolean(formData.invitationToken);
  const isCommitteeInvite =
    formData.inviteRole === "committee" || formData.userType === "committee";
  const introItems = isInvited
    ? [
        {
          letter: "Y",
          label: t("digipingAdminItemYou"),
          desc: t("digipingAdminItemYouDesc"),
        },
        {
          letter: "A",
          label: t("digipingAdminItemAccount"),
          desc: t("digipingAdminItemAccountDescInvited"),
        },
      ]
    : [
        { letter: "C", label: t("digipingAdminItemClub"), desc: t("digipingAdminItemClubDesc") },
        { letter: "Y", label: t("digipingAdminItemYou"), desc: t("digipingAdminItemYouDesc") },
        {
          letter: "A",
          label: t("digipingAdminItemAccount"),
          desc: t("digipingAdminItemAccountDesc"),
        },
      ];

  const renderTray = () => {
    if (isTyping || streamingMsgId) return null;
    const step = wizardStep;
    if (!step) return null;

    if (step === "invitedClub" || step === "healthIntro" || step === "whoCapsules") {
      return (
        <div className="options-tray animate-slide-up">
          {step === "healthIntro" ? (
            <div className="club-admin-health-intro">
              <ScforhApproachIntro variant="compact" tone="dark" />
            </div>
          ) : null}
          {step === "whoCapsules" ? (
            <div className="club-admin-health-intro">
              {formData.scforh?.targetGroups?.length ? (
                <ScforhWhoCapsule
                  targetGroups={formData.scforh.targetGroups}
                  tone="dark"
                  variant="compact"
                />
              ) : (
                <ScforhWhoCapsule
                  bands={["youth", "adults", "seniors"]}
                  tone="dark"
                  variant="compact"
                />
              )}
            </div>
          ) : null}
          <button
            type="button"
            className="tray-card ripple flex-center"
            onClick={() => commit(t("digipingAdminContinue"), {})}
          >
            <ArrowForwardIcon className="tray-icon" />
            <span className="tray-card-title">{t("digipingAdminContinue")}</span>
          </button>
        </div>
      );
    }

    if (step === "clubCountry") {
      const q = countrySearch.trim().toLowerCase();
      const filtered = CLUB_ADMIN_COUNTRIES.filter(
        (c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
      ).slice(0, 18);
      return (
        <div className="options-badge-tray animate-slide-up">
          <div className="search-input-wrapper">
            <SearchIcon className="search-field-icon" />
            <input
              type="text"
              placeholder={t("searchCountry")}
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              className="search-input-field"
              autoFocus={autoFocusField}
            />
          </div>
          <div className="badge-grid">
            {filtered.map((country) => (
              <button
                key={country.id}
                type="button"
                className="badge-button ripple"
                onClick={async () => {
                  const lang = clubCountryLanguage({ country: country.code });
                  if (lang) {
                    try {
                      localStorage.setItem("lng", lang);
                      localStorage.setItem("i18nextLng", lang);
                    } catch {
                      /* ignore */
                    }
                    if (String(i18n.language || "").slice(0, 2) !== lang) {
                      await i18n.changeLanguage(lang);
                    }
                  }
                  commit(`${country.flag} ${country.name}`, {
                    clubCountry: country.name,
                    clubCountryCode: country.code,
                    selectedClub: null,
                    clubSearch: "",
                    clubNotInList: false,
                    federationAffiliated: "",
                    adminProofUrl: "",
                    adminProofName: "",
                  });
                }}
              >
                <span className="flag-span">{country.flag}</span> {country.name}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (step === "federationAffiliation") {
      return (
        <div className="options-tray animate-slide-up">
          <button
            type="button"
            className="tray-card ripple"
            onClick={() => commit(t("federationAffiliatedYes"), { federationAffiliated: "yes" })}
          >
            <span className="tray-card-title">{t("federationAffiliatedYes")}</span>
          </button>
          <button
            type="button"
            className="tray-card ripple"
            onClick={() => commit(t("federationAffiliatedNo"), { federationAffiliated: "no" })}
          >
            <span className="tray-card-title">{t("federationAffiliatedNo")}</span>
          </button>
        </div>
      );
    }

    if (step === "clubSelection") {
      const q = clubSearch.trim().toLowerCase();
      const filtered = clubs.filter(
        (club) =>
          !q ||
          String(club.name || "").toLowerCase().includes(q) ||
          String(club.city || "").toLowerCase().includes(q)
      );
      const showName = clubNotListed || clubs.length === 0;
      return (
        <div className="options-badge-tray animate-slide-up">
          {!showName ? (
            <>
              <div className="search-input-wrapper">
                <SearchIcon className="search-field-icon" />
                <input
                  type="text"
                  placeholder={t("searchClub")}
                  value={clubSearch}
                  onChange={(e) => setClubSearch(e.target.value)}
                  className="search-input-field"
                  autoFocus={autoFocusField}
                />
              </div>
              <div className="badge-grid max-height-tray">
                {filtered.slice(0, 15).map((club) => (
                  <button
                    key={club.club_id || club.id || club.name}
                    type="button"
                    className="badge-button ripple align-left"
                    onClick={() =>
                      commit(club.name, {
                        selectedClub: club,
                        clubName: club.name,
                        clubCity: club.city || "",
                        clubNotInList: false,
                      })
                    }
                  >
                    🏢 {club.name}
                    {club.city ? ` · ${club.city}` : ""}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="tray-card ripple flex-center"
                onClick={() => {
                  setClubNotListed(true);
                  setTextDraft("");
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: `user_${Date.now()}`,
                      sender: "user",
                      text: t("digipingAdminSkipListed"),
                      step: "clubSelection",
                    },
                    {
                      id: `ai_${Date.now()}`,
                      sender: "ai",
                      text: i18n.t("digipingAdminQClubName"),
                      step: "clubSelection",
                      dimension: "C",
                    },
                  ]);
                }}
              >
                <BusinessIcon className="tray-icon" />
                <span className="tray-card-title">{t("digipingAdminSkipListed")}</span>
              </button>
            </>
          ) : (
            <form
              className="inline-text-form"
              onSubmit={(e) => {
                e.preventDefault();
                const name = textDraft.trim();
                if (!name) return;
                if (looksLikeClubNotListed(name)) {
                  setClubNotListed(true);
                  setTextDraft("");
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: `user_${Date.now()}`,
                      sender: "user",
                      text: name,
                      step: "clubSelection",
                    },
                    {
                      id: `ai_${Date.now()}`,
                      sender: "ai",
                      text: i18n.t("digipingAdminQClubName"),
                      step: "clubSelection",
                      dimension: "C",
                    },
                  ]);
                  return;
                }
                commit(name, {
                  clubName: name,
                  selectedClub: null,
                  clubNotInList: true,
                });
              }}
            >
              <div className="search-input-wrapper">
                <BusinessIcon className="search-field-icon" />
                <input
                  type="text"
                  className="inline-input-field"
                  placeholder={t("enterClubName")}
                  value={textDraft}
                  onChange={(e) => setTextDraft(e.target.value)}
                  autoFocus={autoFocusField && !textDraft.trim()}
                />
                <button type="submit" className="inline-send-btn" aria-label={t("digipingAdminConfirm")}>
                  <SendIcon />
                </button>
              </div>
            </form>
          )}
        </div>
      );
    }

    if (step === "clubProof") {
      return (
        <div className="options-tray animate-slide-up">
          <label className="tray-card ripple club-admin-proof-card">
            <input
              type="file"
              accept="application/pdf,.pdf"
              hidden
              disabled={uploadingProof}
              onChange={(e) => handleProofUpload(e.target.files?.[0])}
            />
            <span className="tray-card-title">
              {uploadingProof ? t("uploading") : t("clubProofChooseFile")}
            </span>
          </label>
          {formData.adminProofName ? (
            <p className="club-admin-proof-name">{formData.adminProofName}</p>
          ) : null}
          {renderEmailCheckError()}
        </div>
      );
    }

    if (step === "adminFirstName" || step === "adminLastName") {
      const placeholders = {
        adminFirstName: t("enterAdminFirstName"),
        adminLastName: t("enterAdminLastName"),
      };
      return (
        <form
          className="inline-text-form animate-slide-up"
          onSubmit={(e) => {
            e.preventDefault();
            const value = textDraft.trim();
            if (!value) return;
            if (step === "adminFirstName") commit(value, { adminFirstName: value });
            if (step === "adminLastName") commit(value, { adminLastName: value });
          }}
        >
          <div className="search-input-wrapper">
            <input
              type="text"
              className="inline-input-field"
              placeholder={placeholders[step]}
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              autoFocus={autoFocusField && !textDraft.trim()}
            />
            <button type="submit" className="inline-send-btn" aria-label={t("digipingAdminConfirm")}>
              <SendIcon />
            </button>
          </div>
          {renderEmailCheckError()}
        </form>
      );
    }

    if (step === "adminPosition") {
      const lang = (i18n.language || "en").slice(0, 2);
      return (
        <div className="options-badge-tray animate-slide-up">
          <div className="badge-grid max-height-tray">
            {ADMIN_POSITIONS.map((position) => {
              const label = getPositionName(position, lang);
              return (
                <button
                  key={position.id}
                  type="button"
                  className="badge-button ripple"
                  onClick={() => commit(label, { adminPosition: position.id })}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    const chipMaps = {
      scforhSportingAims: { ids: SCFORH_SPORTING_AIMS, key: "scforhSportingAims", field: "sportingAims", multi: false },
      scforhTargetGroups: { ids: SCFORH_TARGET_GROUPS, key: "scforhTargetGroups", field: "targetGroups", multi: true },
      scforhHealthDimensions: {
        ids: SCFORH_HEALTH_DIMENSIONS,
        key: "scforhHealthDimensions",
        field: "healthDimensions",
        multi: true,
      },
      scforhIntegration: { ids: SCFORH_INTEGRATION, key: "scforhIntegration", field: "integration", multi: false },
      scforhCurrentOffer: { ids: SCFORH_CURRENT_OFFER, key: "scforhCurrentOffer", field: "currentOffer", multi: false },
    };
    if (chipMaps[step]) {
      const spec = chipMaps[step];
      if (spec.multi) {
        const dimStatus =
          spec.field === "healthDimensions"
            ? inferHealthDimensionStatus(formData.scforh)
            : null;
        return (
          <div className="options-badge-tray animate-slide-up">
            <p className="tray-subtitle">
              {spec.field === "healthDimensions"
                ? t("scforhDimGrowHint")
                : t("digipingAdminSelectMultipleHint")}
            </p>
            {dimStatus ? (
              <ScforhDimensionStatus status={dimStatus} tone="dark" />
            ) : null}
            <div className="badge-grid">
              {spec.ids.map((id) => {
                const label = t(`${spec.key}_${id}`);
                const on = multiDraft.includes(id);
                const grow = dimStatus?.underApplied?.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`badge-button ripple ${on ? "active-badge" : ""} ${grow ? "badge-button--grow" : ""}`}
                    onClick={() =>
                      setMultiDraft((prev) =>
                        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                      )
                    }
                  >
                    {label}
                    {grow ? (
                      <span className="badge-grow-tag">{t("scforhDimGrowShort")}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {spec.field === "targetGroups" && multiDraft.length ? (
              <ScforhWhoCapsule
                targetGroups={multiDraft}
                tone="dark"
                variant="compact"
              />
            ) : null}
            <button
              type="button"
              className="wizard-begin-btn ripple"
              disabled={!multiDraft.length}
              onClick={() => {
                const labels = multiDraft.map((id) => t(`${spec.key}_${id}`)).join(", ");
                if (spec.field === "healthDimensions") {
                  const status = inferHealthDimensionStatus(formData.scforh);
                  const grow = status.underApplied.filter((id) =>
                    multiDraft.includes(id)
                  );
                  commit(labels, {
                    scforh: {
                      healthDimensions: multiDraft,
                      likelyAppliedDimensions: status.likelyApplied,
                      underAppliedDimensions: status.underApplied,
                      growDimensions: grow.length ? grow : multiDraft,
                    },
                  });
                  return;
                }
                commit(labels, { scforh: { [spec.field]: multiDraft } });
              }}
            >
              {t("digipingAdminConfirm")}
            </button>
          </div>
        );
      }
      return (
        <div className="options-tray animate-slide-up">
          {spec.ids.map((id) => {
            const label = t(`${spec.key}_${id}`);
            return (
              <button
                key={id}
                type="button"
                className="tray-card ripple"
                onClick={() => commit(label, { scforh: { [spec.field]: id } })}
              >
                <span className="tray-card-title">{label}</span>
              </button>
            );
          })}
        </div>
      );
    }

    if (step === "adminEmail") {
      return (
        <form
          className="inline-text-form animate-slide-up"
          onSubmit={(e) => {
            e.preventDefault();
            handleEmailSubmit();
          }}
        >
          <div className="search-input-wrapper">
            <input
              type="email"
              className="inline-input-field"
              placeholder={t("enterAdminEmail")}
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              autoFocus={autoFocusField && !textDraft.trim()}
            />
            <button type="submit" className="inline-send-btn" disabled={busy} aria-label={t("digipingAdminEmailSend")}>
              <SendIcon />
            </button>
          </div>
          {renderEmailCheckError()}
        </form>
      );
    }

    if (step === "emailConfirmation") {
      return (
        <div className="options-tray animate-slide-up">
          <button type="button" className="tray-card ripple flex-center" onClick={handleCheckEmail} disabled={busy}>
            <span className="tray-card-title">
              {busy ? t("checking") : t("digipingAdminEmailCheck")}
            </span>
          </button>
          <button
            type="button"
            className="tray-card ripple"
            onClick={() => sendConfirmationEmail()}
            disabled={busy}
          >
            <span className="tray-card-title">{t("digipingAdminEmailResend")}</span>
          </button>
          {renderEmailCheckError()}
        </div>
      );
    }

    if (step === "adminPassword") {
      const askExistingPassword =
        isExistingAccount && !formData.restoringDeletedAccount;
      return (
        <form onSubmit={handlePasswordSubmit} className="password-form-tray animate-slide-up">
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={formData.adminEmail || ""}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          />
          <div className="password-fields-row">
            <div className="password-input-group">
              <SecurityIcon className="search-field-icon" />
              <input
                type="password"
                name={askExistingPassword ? "current-password" : "new-password"}
                autoComplete={askExistingPassword ? "current-password" : "new-password"}
                placeholder={askExistingPassword ? t("enterYourPassword") : t("enterPasswordMin6")}
                value={passwordForm.password}
                onChange={(e) => setPasswordForm((prev) => ({ ...prev, password: e.target.value }))}
                className="inline-input-field"
                disabled={busy}
                autoFocus={autoFocusField}
              />
            </div>
            {!askExistingPassword && (
              <div className="password-input-group">
                <SecurityIcon className="search-field-icon" />
                <input
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  placeholder={t("confirmPassword")}
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }))}
                  className="inline-input-field"
                  disabled={busy}
                />
              </div>
            )}
          </div>
          <button type="submit" className="password-submit-btn ripple" disabled={busy}>
            {busy
              ? t("loading")
              : askExistingPassword
                ? t("validateAndJoinClub")
                : t("digipingAdminConfirm")}{" "}
            <ArrowForwardIcon />
          </button>
          {renderEmailCheckError()}
        </form>
      );
    }

    if (step === "affiliationInterest") {
      if (interestSent) return null;
      return (
        <form
          className="options-badge-tray animate-slide-up club-admin-interest-tray"
          onSubmit={(e) => {
            e.preventDefault();
            handleAffiliationSubmit();
          }}
        >
          <input
            className="inline-input-field"
            placeholder={t("enterClubName")}
            value={formData.clubName}
            onChange={(e) => setFormData((p) => ({ ...p, clubName: e.target.value }))}
            required
          />
          <input
            className="inline-input-field"
            type="email"
            placeholder={t("enterAdminEmail")}
            value={formData.adminEmail}
            onChange={(e) => setFormData((p) => ({ ...p, adminEmail: e.target.value }))}
            required
          />
          <input
            className="inline-input-field"
            placeholder={t("enterAdminFirstName")}
            value={formData.adminFirstName}
            onChange={(e) => setFormData((p) => ({ ...p, adminFirstName: e.target.value }))}
          />
          <input
            className="inline-input-field"
            placeholder={t("enterCity")}
            value={formData.clubCity}
            onChange={(e) => setFormData((p) => ({ ...p, clubCity: e.target.value }))}
          />
          <textarea
            className="inline-input-field"
            rows={3}
            placeholder={t("affiliationInterestMessagePlaceholder")}
            value={formData.clubDescription}
            onChange={(e) => setFormData((p) => ({ ...p, clubDescription: e.target.value }))}
          />
          <button type="submit" className="wizard-begin-btn ripple" disabled={busy}>
            {t("sendAffiliationInterest")}
          </button>
          {renderEmailCheckError()}
        </form>
      );
    }

    return null;
  };

  if (inviteLoading) {
    return (
      <div className="invite-splash-screen">
        <div className="invite-boot-card">
          <InviteSplashLogos club={formData.selectedClub} clubName={joinClubName} />
          <p className="intro-body-text">{joinClubName || t("digipingAdminTyping")}</p>
        </div>
      </div>
    );
  }

  if (introStarted && !wizardStep) {
    return (
      <div className="invite-splash-screen">
        <div className="invite-boot-card">
          <DigipingMark size={44} title="Pingflo" />
          <p className="intro-body-text">{t("digipingAdminTyping")}</p>
        </div>
      </div>
    );
  }

  if (error && !introStarted) {
    return (
      <div className="wizard-full-bg">
        <div className="wizard-intro-card">
          <h2>{t("invalidAdminInvite")}</h2>
          <p className="intro-body-text">{error}</p>
        </div>
      </div>
    );
  }

  if (!introStarted) {
    const showSmart = introPhase !== "body";
    const showAction = introPhase === "action" || introPhase === "ready";
    const introReady = introPhase === "ready";
    return (
      <div className="wizard-full-bg">
        <div className="wizard-intro-card animate-fade-in">
          <div className="wizard-intro-card__body">
          <div className="wizard-intro-header">
            <div className="logo-badge logo-badge--digiping">
              <DigipingWizardMark
                clubLogoSrc={clubLogoSrc}
                size={44}
                imgClassName="wizard-join-logo"
                title={joinClubName || "Pingflo"}
              />
            </div>
            <div className="wizard-intro-titles">
              {joinClubName ? (
                <div className="wizard-join-row">
                  <span className="wizard-join-banner">
                    {isInvited
                      ? t("joinClubBanner", { club: joinClubName })
                      : joinClubName}
                  </span>
                  {isInvited ? (
                    <LanguageShortcutFab
                      variant="invite"
                      onPicked={markInviteUiLangOverride}
                    />
                  ) : null}
                </div>
              ) : null}
              <span className="digiping-tag">{t("digipingAdminTag")}</span>
              <h2>
                {isInvited
                  ? t("digipingAdminInviteIntroTitle")
                  : t("digipingAdminIntroTitle")}
              </h2>
              {isInvited ? (
                <p className="onehealth-intro-role">
                  {isCommitteeInvite
                    ? t("digipingAdminInviteRoleCommittee")
                    : t("digipingAdminInviteRoleAdmin")}
                </p>
              ) : null}
            </div>
          </div>

          {introPhase === "body" ? (
            <ClubAdminTypewriter
              text={
                isInvited
                  ? t("digipingAdminInviteIntroText", { club: joinClubName })
                  : t("digipingAdminIntroText")
              }
              className="intro-body-text"
              msPerChar={8}
              onComplete={() => {
                setIntroSmartIdx(0);
                setIntroPhase("smart");
              }}
            />
          ) : (
            <RichText
              text={
                isInvited
                  ? t("digipingAdminInviteIntroText", { club: joinClubName })
                  : t("digipingAdminIntroText")
              }
              className="intro-body-text"
            />
          )}
          {introPhase !== "body" ? (
            <div className="club-admin-health-intro club-admin-health-intro--invite">
              <SportClubsOneHealthIntro clubName={joinClubName} variant="compact" tone="dark" />
            </div>
          ) : null}

          {showSmart ? (
            <div className="smart-grid" aria-live="polite">
              {introItems.map((item, i) => {
                const revealed = introPhase !== "smart" || i < introSmartIdx;
                const streaming = introPhase === "smart" && i === introSmartIdx;
                if (!revealed && !streaming) return null;
                return (
                  <div
                    key={item.letter}
                    className={`smart-bullet smart-bullet--${item.letter.toLowerCase()}${
                      streaming ? " smart-bullet--streaming" : ""
                    }`}
                  >
                    <span className="smart-letter" aria-hidden="true">
                      {item.letter}
                    </span>
                    <div className="smart-bullet-copy">
                      <span className="smart-label">{item.label}</span>
                      {revealed ? (
                        <span className="smart-desc">{item.desc}</span>
                      ) : (
                        <ClubAdminTypewriter
                          text={item.desc}
                          className="smart-desc"
                          msPerChar={7}
                          richWhileStreaming={false}
                          onComplete={() => {
                            if (i + 1 >= introItems.length) setIntroPhase("action");
                            else setIntroSmartIdx(i + 1);
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {showAction ? (
            introPhase === "action" ? (
              <ClubAdminTypewriter
                text={
                  isInvited
                    ? t("digipingAdminInviteIntroAction")
                    : t("digipingAdminIntroAction")
                }
                className="intro-action-prompt"
                msPerChar={9}
                onComplete={() => setIntroPhase("ready")}
              />
            ) : (
              <p className="intro-action-prompt">
                {isInvited
                  ? t("digipingAdminInviteIntroAction")
                  : t("digipingAdminIntroAction")}
              </p>
            )
          ) : null}

          </div>
          <div className="wizard-intro-card__cta">
          {introReady ? (
            <button type="button" onClick={startWizard} className="wizard-begin-btn ripple">
              {isInvited ? t("digipingAdminInviteStartBtn") : t("digipingAdminStartBtn")}{" "}
              <ArrowForwardIcon />
            </button>
          ) : (
            <div className="intro-stream-status" aria-live="polite">
              <span className="typing-indicator-dots">
                <span />
                <span />
                <span />
              </span>
              <span className="intro-stream-status__label">{t("digipingAdminTyping")}</span>
            </div>
          )}
          </div>
        </div>
      </div>
    );
  }

  const pct = Math.round(((Math.max(0, steps.indexOf(wizardStep)) + 1) / Math.max(steps.length, 1)) * 100);

  return (
    <div className="wizard-full-bg">
      <div className="wizard-chat-container">
        <div className="wizard-chat-header">
            <div className="wizard-chat-header__brand">
              <div className="coach-avatar coach-avatar--header" title={joinClubName || "Pingflo"}>
                <DigipingWizardMark
                  clubLogoSrc={clubLogoSrc}
                  size={28}
                  imgClassName="wizard-join-logo wizard-join-logo--sm"
                  title={joinClubName || "Pingflo"}
                />
              </div>
            <div className="wizard-chat-header__text">
              {joinClubName ? (
                <span className="wizard-join-banner wizard-join-banner--compact">{joinClubName}</span>
              ) : null}
              <span className="coach-label">{t("digipingAdminCoachName")}</span>
              <span className="digiping-tag">
                {t(`digipingAdminPhase${phase === "C" ? "Club" : phase === "Y" ? "You" : phase === "H" ? "Health" : "Account"}`)}
              </span>
            </div>
          </div>
          <div className="club-admin-progress" aria-hidden="true">
            <div className="club-admin-progress__fill" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="wizard-chat-history" ref={chatHistoryRef}>
          {messages.map((msg, index) => {
            if (msg.isSystem) {
              return (
                <div key={msg.id || index} className="system-row" data-msg-id={msg.id}>
                  <div className="system-msg-box animate-scale-up">
                    <RichText text={msg.text} />
                  </div>
                </div>
              );
            }
            const isStreaming = msg.sender === "ai" && msg.id === streamingMsgId;
            const dim = msg.dimension || phase;
            return (
              <div
                key={msg.id || index}
                className={`${msg.sender === "ai" ? "ai-row" : "user-row"} animate-fade-in`}
                data-msg-id={msg.id}
              >
                {msg.sender === "ai" ? (
                  <div className="coach-avatar" title="Pingflo">
                    <DigipingMark size={30} title="Pingflo" />
                  </div>
                ) : null}
                <div className={`${msg.sender === "ai" ? "ai-bubble" : "user-bubble"} glass-item`}>
                  {msg.sender === "ai" ? (
                    <div className="coach-label-row">
                      <span className="coach-label">{t("digipingAdminCoachName")}</span>
                      {dim ? (
                        <span className={`dimension-chip dp-em dp-em--${String(dim).toLowerCase()}`}>
                          {dim} · {t(`digipingAdminPhase${dim === "C" ? "Club" : dim === "Y" ? "You" : dim === "H" ? "Health" : "Account"}`)}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {isStreaming ? (
                    <ClubAdminTypewriter
                      text={msg.text}
                      onTick={() => scrollToEnd("auto")}
                      onComplete={() => setStreamingMsgId(null)}
                    />
                  ) : msg.sender === "ai" ? (
                    <RichText text={msg.text} className="msg-content-text" />
                  ) : (
                    <div className="user-answer-row">
                      <p className="msg-content-text">{msg.text}</p>
                      {msg.step &&
                      msg.id === [...messages].reverse().find((m) => m.sender === "user")?.id &&
                      !isTyping &&
                      !streamingMsgId ? (
                        <button
                          type="button"
                          className="edit-answer-btn"
                          onClick={beginEditLast}
                        >
                          <EditOutlinedIcon fontSize="small" />
                          <span>{t("digipingAdminEdit", { defaultValue: "Edit" })}</span>
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {isTyping ? (
            <div className="ai-row animate-fade-in">
              <div className="coach-avatar" title="Pingflo">
                <DigipingMark size={30} title="Pingflo" />
              </div>
              <div className="ai-bubble glass-item typing-bubble">
                <div className="coach-label-row">
                  <span className="coach-label">{t("digipingAdminCoachName")}</span>
                </div>
                <div className="typing-indicator-dots">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          ) : null}
          <div ref={chatEndRef} className="wizard-chat-history__spacer" aria-hidden="true" />
        </div>

        <div className="wizard-interactive-footer">
          {renderTray()}
          {error ? <p className="inline-error-text">{error}</p> : null}
        </div>
      </div>
    </div>
  );
};

export default ClubAdminProfiling;
